<template>
  <media-with-thread-chat
    :event="event"
    :filecookie="activeFilecookie"
    :content-kind="contentKind"
    :files="viewableFiles"
    :current-index="currentIndex"
    @select-file="onSelectFile"
  >
    <component
      :is="viewerComponent"
      v-if="viewerComponent"
      :key="activeFilecookie"
      class="min-h-0 flex-1"
      v-bind="viewerProps"
    />
  </media-with-thread-chat>
</template>

<script setup lang="ts">
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent } from '@wui/im'
import { computed, ref, shallowRef, watch } from 'vue'

import { resolveAttachmentContentKind } from '../lib/file-type-utils'
import { loadDocumentViewerComponent } from '../lib/open-attachment-viewer'
import { getEventAttachmentFiles, getViewableFiles } from '../lib/viewable-files'

import MediaWithThreadChat from './media-with-thread-chat.vue'

const props = defineProps<{
  filecookie: string
  event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
}>()

const viewerComponent = shallowRef<object | null>(null)
const activeFilecookie = ref(props.filecookie)

const viewableFiles = computed(() => {
  return getViewableFiles(getEventAttachmentFiles(props.event, activeFilecookie.value))
})

const currentIndex = computed(() => {
  const index = viewableFiles.value.findIndex(file => file.filecookie === activeFilecookie.value)
  return index >= 0 ? index : 0
})

const currentFile = computed(() => viewableFiles.value[currentIndex.value])

const contentKind = computed(() => {
  if (!currentFile.value) {
    return null
  }
  return resolveAttachmentContentKind(currentFile.value)
})

const documentSrc = computed(() => `/files/${activeFilecookie.value}`)

const viewerProps = computed(() => {
  const file = currentFile.value
  if (!file || !contentKind.value) {
    return {}
  }

  const baseProps = {
    src: documentSrc.value,
    event: props.event,
    filecookie: activeFilecookie.value,
  }

  if (contentKind.value === 'media') {
    return {
      ...baseProps,
      file,
    }
  }

  return baseProps
})

const onSelectFile = (index: number) => {
  const file = viewableFiles.value[index]
  if (file) {
    activeFilecookie.value = file.filecookie
  }
}

watch(() => props.filecookie, (filecookie) => {
  activeFilecookie.value = filecookie
})

watch(contentKind, async (kind, previousKind) => {
  if (!kind) {
    viewerComponent.value = null
    return
  }

  if (kind !== previousKind) {
    viewerComponent.value = null
  }

  viewerComponent.value = await loadDocumentViewerComponent(kind)
}, { immediate: true })
</script>
